export const USER_FEEDBACK_DATA_TYPES = [
  "NUMERIC",
  "CATEGORICAL",
  "BOOLEAN",
] as const;
export type UserFeedbackDataType = (typeof USER_FEEDBACK_DATA_TYPES)[number];

export const USER_FEEDBACK_SOURCES = ["USER", "EVAL", "SYSTEM"] as const;
export type UserFeedbackSource = (typeof USER_FEEDBACK_SOURCES)[number];

export const NAMI_CHAT_THUMBS = {
  subjectType: "nami_chat_message",
  scoreName: "thumbs",
} as const;

/**
 * Ship & Debit 849 expert labels — every user action on the 849 inbox
 * (/ship-and-debit/849-inbox) is persisted as a label so the email classifier /
 * matcher can be trained and evaluated against expert ground truth. The label row's
 * metadata carries the engine's PREDICTION at action time (detected supplier,
 * confidence signals, surfaced candidates), making each row a ready-made
 * prediction-vs-truth training pair.
 */
export const SD_849_LABELS = {
  /** Is this inbound email a supplier 849 response? (the classifier's core question) */
  disposition: {
    subjectType: "comm_inbound_email",
    scoreName: "sd_849_disposition",
  },
  /** Which S&D supplier this email belongs to (value = company/supplier UUID). */
  supplier: {
    subjectType: "comm_inbound_email",
    scoreName: "sd_849_supplier",
  },
  /** Claim identity: does this email belong to an existing 849 response?
   *  (value = a SD849ClaimIdentityValue; target response id in metadata) */
  claimIdentity: {
    subjectType: "comm_inbound_email",
    scoreName: "sd_849_claim_identity",
  },
  /** Expert pinned an 849 line to a specific 844 detail line (ground truth for the
   *  matcher; line index + sd_e844d id in metadata). */
  lineMatch: {
    subjectType: "sd_849_response",
    scoreName: "sd_849_line_match",
  },
} as const;

export const SD_849_DISPOSITION_VALUES = [
  /** The email IS a supplier 849 response (created / attached with line decisions). */
  "is_849",
  /** The email is NOT an 849 (dismissed; any wrongly-created response deleted). */
  "not_849",
  /** The email is not itself an 849, but is context/thread for an existing one. */
  "context_for_849",
] as const;
export type SD849DispositionValue = (typeof SD_849_DISPOSITION_VALUES)[number];

export const SD_849_CLAIM_IDENTITY_VALUES = [
  /** Expert attached this email onto an existing 849 (positive same-claim label). */
  "attached",
  /** Expert attached as context only (same claim, no line decisions). */
  "attached_context",
  /** Expert created a NEW 849 even though candidates were surfaced (negative label:
   *  the surfaced signals did NOT mean same-claim; candidates in metadata). */
  "new_despite_candidates",
] as const;
export type SD849ClaimIdentityValue =
  (typeof SD_849_CLAIM_IDENTITY_VALUES)[number];

/**
 * Demand signal for features a user cannot reach yet — the "alert me when this
 * is ready" CTA on the feature-preview pages. `subjectId` is the feature key
 * (see FEATURE_INTEREST_KEYS), so one row per user per feature tells us who
 * asked for what, and the optional comment carries what they'd use it for.
 */
export const FEATURE_INTEREST = {
  subjectType: "feature_interest",
  scoreName: "early_access",
} as const;

/** Features with a preview page. Used as the feedback row's `subjectId`. */
export const FEATURE_INTEREST_KEYS = [
  "agents",
  "tasks",
  "tools",
  "workflows",
] as const;
export type FeatureInterestKey = (typeof FEATURE_INTEREST_KEYS)[number];

export const USER_FEEDBACK_SUBJECT_TYPES = [
  NAMI_CHAT_THUMBS.subjectType,
  SD_849_LABELS.disposition.subjectType,
  SD_849_LABELS.lineMatch.subjectType,
  FEATURE_INTEREST.subjectType,
] as const;
export type UserFeedbackSubjectType =
  (typeof USER_FEEDBACK_SUBJECT_TYPES)[number];

export interface UserFeedbackConfigDef {
  subjectType: UserFeedbackSubjectType;
  scoreName: string;
  label: string;
  description: string | null;
  dataType: UserFeedbackDataType;
  numericMin: number | null;
  numericMax: number | null;
  numericStep: number | null;
  categoricalOptions: Array<{ value: string; label: string }> | null;
  commentEnabled: boolean;
  commentRequired: boolean;
  sortOrder: number;
}

export const USER_FEEDBACK_CONFIGS: readonly UserFeedbackConfigDef[] = [
  {
    subjectType: NAMI_CHAT_THUMBS.subjectType,
    scoreName: NAMI_CHAT_THUMBS.scoreName,
    label: "Was Nami's response helpful?",
    description: null,
    dataType: "BOOLEAN",
    numericMin: null,
    numericMax: null,
    numericStep: null,
    categoricalOptions: null,
    commentEnabled: true,
    commentRequired: false,
    sortOrder: 0,
  },
  {
    subjectType: SD_849_LABELS.disposition.subjectType,
    scoreName: SD_849_LABELS.disposition.scoreName,
    label: "Is this email a supplier 849 response?",
    description:
      "Expert disposition of an inbound S&D email — ground truth for the 849 classifier.",
    dataType: "CATEGORICAL",
    numericMin: null,
    numericMax: null,
    numericStep: null,
    categoricalOptions: [
      { value: "is_849", label: "Is an 849" },
      { value: "not_849", label: "Not an 849" },
      { value: "context_for_849", label: "Context for an 849" },
    ],
    commentEnabled: true,
    commentRequired: false,
    sortOrder: 0,
  },
  {
    subjectType: SD_849_LABELS.supplier.subjectType,
    scoreName: SD_849_LABELS.supplier.scoreName,
    label: "Which supplier sent this 849?",
    description:
      "Expert-confirmed supplier for an inbound S&D email (value = supplier UUID).",
    dataType: "CATEGORICAL",
    numericMin: null,
    numericMax: null,
    numericStep: null,
    // Options are the org's S&D suppliers — dynamic, so not enumerable here.
    categoricalOptions: null,
    commentEnabled: false,
    commentRequired: false,
    sortOrder: 1,
  },
  {
    subjectType: SD_849_LABELS.claimIdentity.subjectType,
    scoreName: SD_849_LABELS.claimIdentity.scoreName,
    label: "Does this email belong to an existing 849?",
    description:
      "Expert attach-vs-create decision — trains claim identity (which emails are one claim).",
    dataType: "CATEGORICAL",
    numericMin: null,
    numericMax: null,
    numericStep: null,
    categoricalOptions: [
      { value: "attached", label: "Attached to existing" },
      { value: "attached_context", label: "Attached as context" },
      { value: "new_despite_candidates", label: "New despite candidates" },
    ],
    commentEnabled: false,
    commentRequired: false,
    sortOrder: 2,
  },
  {
    subjectType: SD_849_LABELS.lineMatch.subjectType,
    scoreName: SD_849_LABELS.lineMatch.scoreName,
    label: "Line pinned to an 844 detail line",
    description:
      "Expert pinned an 849 response line to a specific sd_e844d line — matcher ground truth.",
    dataType: "CATEGORICAL",
    numericMin: null,
    numericMax: null,
    numericStep: null,
    categoricalOptions: null,
    commentEnabled: false,
    commentRequired: false,
    sortOrder: 3,
  },
  {
    subjectType: FEATURE_INTEREST.subjectType,
    scoreName: FEATURE_INTEREST.scoreName,
    label: "Notify me when this feature is ready",
    description:
      "A user asked for early access to a previewed feature (subjectId = feature key).",
    dataType: "BOOLEAN",
    numericMin: null,
    numericMax: null,
    numericStep: null,
    categoricalOptions: null,
    commentEnabled: true,
    commentRequired: false,
    sortOrder: 0,
  },
];

export function findFeedbackConfig(
  subjectType: UserFeedbackSubjectType,
  scoreName: string,
): UserFeedbackConfigDef | undefined {
  return USER_FEEDBACK_CONFIGS.find(
    (c) => c.subjectType === subjectType && c.scoreName === scoreName,
  );
}

export function listFeedbackConfigs(
  subjectType: UserFeedbackSubjectType,
): UserFeedbackConfigDef[] {
  return USER_FEEDBACK_CONFIGS.filter(
    (c) => c.subjectType === subjectType,
  ).sort((a, b) => a.sortOrder - b.sortOrder);
}
